import { useState } from 'react';
import { Modal } from './Modal';
import { Tooltip } from './Tooltip';

export interface ItemAction {
  label: string;
  description?: string;
  tone?: 'default' | 'danger';
  disabled?: boolean;
  onSelect: () => void;
}

/**
 * Agrupa acciones secundarias en una hoja móvil/diálogo. Evita que nombres o
 * botones largos conviertan cada elemento de una lista en varias filas.
 */
export function ItemActionsMenu({
  itemLabel,
  actions,
  title = 'Más acciones',
  className = '',
}: {
  itemLabel: string;
  actions: ItemAction[];
  title?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip content={`Ver opciones para ${itemLabel}`} align="end">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`tap-target flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-xl font-bold leading-none text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 ${className}`}
          aria-label={`Más acciones para ${itemLabel}`}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          <span aria-hidden>⋯</span>
        </button>
      </Tooltip>
      <Modal
        open={open}
        title={title}
        eyebrow={itemLabel}
        protectUnsavedChanges={false}
        onClose={() => setOpen(false)}
      >
        <div className="space-y-2">
          {actions.map((action) => (
            <button
              key={action.label}
              type="button"
              disabled={action.disabled}
              onClick={() => {
                setOpen(false);
                action.onSelect();
              }}
              className={`tap-target flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left transition disabled:opacity-50 ${
                action.tone === 'danger'
                  ? 'border-red-100 bg-red-50/60 text-red-700 hover:bg-red-50'
                  : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'
              }`}
            >
              <span className="min-w-0">
                <span className="block text-sm font-semibold">{action.label}</span>
                {action.description && (
                  <span className="mt-0.5 block text-xs leading-5 text-slate-600">
                    {action.description}
                  </span>
                )}
              </span>
              <span className="shrink-0 text-slate-400" aria-hidden>
                ›
              </span>
            </button>
          ))}
        </div>
      </Modal>
    </>
  );
}
